/**
 * @fileoverview Our cases, as the BROWSER archetype sees them.
 *
 * WHY AN ADAPTER AND NOT A PAGE. foundry-ui's `compass-container` is a
 * hand-refined reconstruction of Compass built with the capture open, and it
 * takes a `BrowserGateway` rather than data — the same port `layout-files-folder`
 * implements, from a different app cut by a different team. Its own note is the
 * point:
 *
 *     "The two screens look nothing alike and take the same data, which only
 *      works because rows are keyed by COLUMN: Compass's six headings and the
 *      Files folder's three are both just `columns`."
 *
 * So Files stops being a layout we maintain and becomes a mapping we maintain.
 * The layout is theirs, measured against the real thing; what is ours is which
 * columns a case has and what its filters mean.
 *
 * NARROWING GOES THROUGH `load()`, SORTING DOES NOT — their design decision and
 * the right one: a consumer with a million files cannot filter them in the
 * browser, but the column someone clicked is view state no backend needs. Ours
 * filters client-side today because `listCases` returns the agency's cases in
 * one call; the moment that pages, this is the seam where it changes and no
 * component has to know.
 */

import type {
  BrowserContents,
  BrowserGateway,
  BrowserQuery,
} from "../../components/compass/browser/ports";
import type { Case } from "../../lib/api";

/** Blueprint intent per case status, so the tag colour carries meaning. */
const STATUS_ICON: Record<string, string> = {
  open: "confirm",
  suspended: "pause",
  closed: "lock",
  archived: "box",
};

export function makeCaseBrowserGateway(cases: readonly Case[]): BrowserGateway {
  return {
    load(query?: BrowserQuery): BrowserContents {
      const q = (query?.search ?? "").trim().toLowerCase();
      const selectedStatus = query?.filters?.["Status"] ?? [];
      const selectedType = query?.filters?.["Type"] ?? [];

      const matches = (c: Case) =>
        (q === "" ||
          c.title.toLowerCase().includes(q) ||
          c.caseNumber.toLowerCase().includes(q) ||
          (c.leadDetective ?? "").toLowerCase().includes(q)) &&
        (selectedStatus.length === 0 || selectedStatus.includes(c.status)) &&
        (selectedType.length === 0 || selectedType.includes(c.type));

      const visible = cases.filter(matches);

      /*
        Counts come from the WHOLE set, not the filtered one. A facet that
        recounts itself as you select it tells you how many are left rather than
        how many there are, and the number moves under the cursor.
      */
      const countBy = (pick: (c: Case) => string) => {
        const m = new Map<string, number>();
        for (const c of cases) m.set(pick(c), (m.get(pick(c)) ?? 0) + 1);
        return [...m].sort((a, b) => b[1] - a[1]);
      };

      return {
        breadcrumb: [
          { label: "All files", icon: "folder-close", href: "/workspace/compass" },
          { label: "Investigations", icon: "git-repo", menu: true },
        ],
        searchPlaceholder: "Search all cases, evidence and people…",
        newLabel: "New",
        listTitle: "All files",
        emptyTitle: q || selectedStatus.length || selectedType.length ? "No matches" : "No cases",
        emptyMessage:
          q || selectedStatus.length || selectedType.length
            ? "Try a different term or clear the filters."
            : "Cases opened by this agency appear here.",
        emptyIcon: "folder-open",

        /*
          THE QUICK FILTERS NAME WHAT THIS PRODUCT HAS. Upstream they are
          Portfolios, Projects and Promoted items — Foundry's containers, which
          have no counterpart here. Copying the words would put three cards on
          the page explaining concepts the software does not have.
        */
        quickFilters: [
          {
            title: "Open cases",
            description: "Investigations still being worked. The default view for most detectives.",
            icon: "confirm",
            applyLabel: "Apply",
          },
          {
            title: "Flagged",
            description: "Cases someone marked as needing attention, across every status.",
            icon: "flag",
            applyLabel: "Apply",
          },
          {
            title: "Mine",
            description: "Cases where you are the lead detective.",
            icon: "user",
            applyLabel: "Apply",
          },
        ],
        quickFiltersHideLabel: "Hide",

        filters: [
          {
            name: "Status",
            kind: "multiple",
            searchPlaceholder: "Search statuses…",
            total: cases.length,
            options: countBy((c) => c.status).map(([label, count]) => ({
              label,
              count,
              icon: STATUS_ICON[label] ?? "dot",
              selected: selectedStatus.includes(label),
            })),
          },
          {
            name: "Type",
            kind: "multiple",
            searchPlaceholder: "Search types…",
            total: cases.length,
            options: countBy((c) => c.type).map(([label, count]) => ({
              label,
              count,
              selected: selectedType.includes(label),
            })),
          },
          {
            name: "Lead detective",
            kind: "multiple",
            searchPlaceholder: "Search detectives…",
            options: countBy((c) => c.leadDetective ?? "Unassigned").map(([label, count]) => ({
              label,
              count,
            })),
          },
        ],

        /*
          Six columns, as upstream has six. `info` on the counts because
          "Evidence 12" is ambiguous about whether that is items or pages, and a
          tooltip is cheaper than a longer heading.
        */
        columns: [
          { key: "name", label: "File name", sortable: true },
          { key: "evidence", label: "Evidence", info: "Items of evidence attached to the case" },
          { key: "people", label: "People", info: "Participants recorded on the case" },
          { key: "detective", label: "Lead detective", sortable: true },
          { key: "status", label: "Status" },
          { key: "modified", label: "Last modified", sortable: true },
        ],

        rows: visible.map((c) => ({
          id: c.id,
          label: c.title,
          // The case number is the path-line under the name, which is where the
          // capture puts a resource's location — and a case number IS how this
          // product locates a case.
          secondary: c.caseNumber,
          icon: "folder-close",
          href: `/workspace/compass/view/${c.id}`,
          favorited: c.flagged,
          cells: {
            evidence: String(c.evidenceCount),
            people: String(c.personCount),
            detective: c.leadDetective ?? "Unassigned",
            status: c.status,
            modified: c.updatedAt.slice(0, 10),
          },
        })),
      };
    },
  };
}
